import { Card, Flex, SimpleTable, Steps, Typography } from "djuno-design";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const StepsPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Steps</Title>
          <Text size="sm">
            The Steps component in Djuno Design allows you to create a visual
            representation of progress or navigation through multiple steps in a
            process. Each step can be clickable, and the component highlights
            the active step and previous steps to show progression
          </Text>

          <Title level={5} className="mt-8">
            Features and Customization
          </Title>
          <Text size="sm"></Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Progress Indicator
                </Text>
                : Displays the steps and indicates which steps are completed,
                which one is active, and those yet to come
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Clickable Steps
                </Text>
                : You can optionally provide a callback for each step, making it
                interactive.
              </Text>
            </li>
          </ul>

          <Title level={5} className="mt-8">
            Step Interaction
          </Title>
          <Text size="sm">
            The Steps component determines which steps are active, completed, or
            upcoming by comparing the current step prop with the steps defined
            in the steps array
          </Text>
          <Title level={6} className="mt-4">
            Active and Completed Steps
          </Title>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                Steps before the active step are considered completed.
              </Text>
            </li>
            <li>
              <Text size="sm">
                The active step is visually highlighted to show the user's
                current position in the process
              </Text>
            </li>
            <li>
              <Text size="sm">
                Each completed step is marked with a check icon, while the
                upcoming steps are numbered
              </Text>
            </li>
          </ul>

          <Title level={5} className="mt-8">
            Clickable Steps
          </Title>
          <Text size="sm">
            If a step has a callback function, it becomes clickable. When a
            completed step is clicked, the callback is triggered, allowing users
            to navigate back to previous steps or perform other actions.
          </Text>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="w-full">
          <Title level={5} className="mt-5">
            Examples
          </Title>

          <ExpandableCard
            title="Steps"
            className="w-full"
            codeViewerProps={{
              code: StepsExample1,
            }}
          >
            <Flex direction={"col"} className="gap-3 w-full">
              <Steps
                steps={[
                  { label: "label 1", value: "1" },
                  { label: "label 2", value: "2" },
                ]}
                step="1"
              />
              <Steps
                steps={[
                  { label: "label 1", value: "1" },
                  { label: "label 2", value: "2" },
                  { label: "label 3", value: "3" },
                ]}
                step="2"
              />
            </Flex>
          </ExpandableCard>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable containerClassName="!min-h-[100px]">
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* steps */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">type</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Array of step objects that define each step's label,
                        value, and optional callback.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Steps.ts#L27"}
                        size="xs"
                        code
                      >
                        {`Array<StepItem>`}
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">required</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* step */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">step</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The current active step's value, used to determine the
                        progress.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">string</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">required</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const StepsExample1 = `<Flex direction={"col"} className="gap-3 w-full">
<Steps
  steps={[
    { label: "label 1", value: "1" },
    { label: "label 2", value: "2" },
  ]}
  step="1"
/>
<Steps
  steps={[
    { label: "label 1", value: "1" },
    { label: "label 2", value: "2" },
    { label: "label 3", value: "3" },
  ]}
  step="2"
/>
</Flex>`;

export default StepsPage;
